// Price Changes — read-only audit for the SUPERADMIN.
// The admin corrects prices at the Payments checkpoint (the counter figure
// often lands ₹500–1000 off what was entered). The superadmin only ever sees
// the final number, so every correction is recorded here with WHO, WHEN,
// old → new, and WHY. Changes saved without a reason are flagged.

import React, { useCallback, useEffect, useState } from 'react';
import {
  FiAlertTriangle,
  FiEdit3,
  FiRefreshCw,
  FiSearch,
  FiTrendingDown,
  FiTrendingUp,
  FiUser,
} from 'react-icons/fi';
import MainLayout from '../components/layout/MainLayout.jsx';
import { Alert } from '../components/common/index.js';
import axiosInstance from '../api/axiosInstance';
import { API_ENDPOINTS } from '../config/api.config';

const fmtMoney = (v) => {
  if (v == null || v === '') return '—';
  const n = Number(v);
  return Number.isFinite(n) ? `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 2 })}` : '—';
};

const PERIODS = [
  { value: 'day', label: 'Today' },
  { value: 'week', label: 'This week' },
  { value: 'month', label: 'This month' },
  { value: 'all', label: 'All time' },
];

const SOURCES = [
  { value: '', label: 'Everything' },
  { value: 'inquiry', label: 'Salesperson sales' },
  { value: 'counter', label: 'Counter sales' },
];

const StatCard = ({ icon: Icon, label, value, tone = 'slate', hint }) => {
  const tones = {
    rose: 'bg-rose-50 text-rose-600',
    emerald: 'bg-emerald-50 text-emerald-600',
    amber: 'bg-amber-50 text-amber-600',
    slate: 'bg-slate-100 text-slate-600',
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-center gap-3">
        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${tones[tone]}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <p className="truncate text-xs font-semibold uppercase tracking-wide text-slate-400">{label}</p>
          <p className="truncate text-lg font-bold text-slate-900">{value}</p>
          {hint && <p className="truncate text-xs text-slate-400">{hint}</p>}
        </div>
      </div>
    </div>
  );
};

const ChangeRow = ({ row }) => {
  const diff = Number(row.difference);
  const down = Number.isFinite(diff) && diff < 0;
  const flat = !Number.isFinite(diff) || diff === 0;
  const counter = row.source === 'counter';
  return (
    <div className={`rounded-xl border bg-white p-4 shadow-sm ${row.reason ? 'border-slate-200' : 'border-amber-200'}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-1.5">
            <h3 className="truncate text-base font-bold text-slate-900">{row.customer_name || 'Customer'}</h3>
            <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-bold ${counter ? 'border-violet-200 bg-violet-50 text-violet-700' : 'border-sky-200 bg-sky-50 text-sky-700'}`}>
              {row.source_label}
            </span>
            {row.deleted && (
              <span className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-bold text-slate-400">
                sale deleted
              </span>
            )}
          </div>
          <p className="mt-0.5 text-sm text-slate-500">
            {row.customer_phone}{row.car ? ` · ${row.car}` : ''}
          </p>
          <p className="mt-0.5 text-xs font-medium text-slate-400">
            sold by {row.sold_by || '-'} · changed by <span className="font-bold text-slate-500">{row.changed_by}</span> · {row.changed_at}
          </p>
        </div>

        <div className="flex flex-col items-end gap-1">
          <div className="flex items-center gap-2 text-sm font-bold">
            <span className="text-slate-400 line-through">{fmtMoney(row.old_price)}</span>
            <span className="text-slate-300">→</span>
            <span className="text-slate-900">{fmtMoney(row.new_price)}</span>
          </div>
          {!flat && (
            <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-bold ${down ? 'border-rose-200 bg-rose-50 text-rose-600' : 'border-emerald-200 bg-emerald-50 text-emerald-700'}`}>
              {down ? <FiTrendingDown className="h-3 w-3" /> : <FiTrendingUp className="h-3 w-3" />}
              {down ? '−' : '+'}{fmtMoney(Math.abs(diff))}
            </span>
          )}
        </div>
      </div>

      {row.reason ? (
        <p className="mt-3 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2.5 text-sm text-slate-700">
          <span className="font-bold text-slate-500">Reason: </span>{row.reason}
        </p>
      ) : (
        <p className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-amber-100 bg-amber-50 px-3 py-2 text-xs font-bold text-amber-700">
          <FiAlertTriangle className="h-3.5 w-3.5" /> No reason given — ask {row.changed_by || 'the admin'}
        </p>
      )}
    </div>
  );
};

const PriceChangesPage = () => {
  const [period, setPeriod] = useState('month');
  const [source, setSource] = useState('');
  const [unexplained, setUnexplained] = useState(false);
  const [q, setQ] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { period };
      if (source) params.source = source;
      if (unexplained) params.unexplained = 1;
      if (q) params.q = q;
      const res = await axiosInstance.get(API_ENDPOINTS.PRICE_CHANGES.LIST, { params });
      setData(res.data ?? res);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Could not load the price changes');
    } finally {
      setLoading(false);
    }
  }, [period, source, unexplained, q]);

  useEffect(() => { load(); }, [load]);

  const rows = data?.changes ?? [];
  const noReason = data?.unexplained_count ?? 0;

  return (
    <MainLayout>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-black text-slate-900">Price Changes</h1>
          <p className="mt-1 text-sm text-slate-500">
            Every price the admin corrected, and why — read-only
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-500 transition-colors hover:border-violet-300 hover:bg-violet-50 hover:text-violet-600 disabled:opacity-50"
          aria-label="Refresh"
          title="Refresh"
        >
          <FiRefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {error && <div className="mb-4"><Alert type="error" message={error} /></div>}

      <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard icon={FiEdit3} label="Prices changed" value={data?.count ?? '—'} />
        <StatCard
          icon={FiTrendingDown}
          label="Given away"
          value={fmtMoney(data?.total_reduced)}
          tone="rose"
          hint="below the quoted price"
        />
        <StatCard
          icon={FiTrendingUp}
          label="Collected extra"
          value={fmtMoney(data?.total_increased)}
          tone="emerald"
          hint="above the quoted price"
        />
        <StatCard
          icon={FiAlertTriangle}
          label="No reason given"
          value={noReason}
          tone={noReason > 0 ? 'amber' : 'slate'}
          hint={noReason > 0 ? 'needs an explanation' : 'every change explained'}
        />
      </div>

      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {PERIODS.map((p) => (
            <button
              key={p.value}
              type="button"
              onClick={() => setPeriod(p.value)}
              className={`rounded-full border px-3.5 py-1.5 text-sm font-bold transition-all ${period === p.value ? 'border-violet-500 bg-violet-600 text-white shadow-sm' : 'border-slate-200 bg-white text-slate-600 hover:border-violet-300'}`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={source}
            onChange={(e) => setSource(e.target.value)}
            className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-600 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-300"
          >
            {SOURCES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <button
            type="button"
            onClick={() => setUnexplained((v) => !v)}
            className={`inline-flex items-center gap-1.5 rounded-xl border px-3 py-2 text-sm font-bold transition-all ${unexplained ? 'border-amber-400 bg-amber-50 text-amber-700' : 'border-slate-200 bg-white text-slate-600 hover:border-amber-300'}`}
          >
            <FiAlertTriangle className="h-4 w-4" /> No reason only
          </button>
          <div className="relative">
            <FiSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Customer, car, reason or admin…"
              className="w-64 rounded-xl border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-300"
            />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="py-16 text-center text-sm text-slate-400">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-200 bg-white py-14 text-center">
          <FiUser className="mx-auto mb-2 h-8 w-8 text-slate-200" />
          <p className="text-sm text-slate-400">
            {unexplained ? 'Every price change has a reason' : 'No price was changed in this period'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((row) => <ChangeRow key={row.id} row={row} />)}
        </div>
      )}
    </MainLayout>
  );
};

export default PriceChangesPage;
